import { observer } from "mobx-react";
import { Dialog } from "@/components-v2/Dialog";
import { useTranslation } from "@/stores/RootStoreContext";
import { Box, VBox } from "@/components-v2/Box";
import { BatchPreviewModel } from "./batchPreviewDomain";
import { Button, IconButton } from "@/components-v2/Button";
import { Select } from "@/components-v2/Select";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { FilePreview, FilePreviewProps } from "@/components-v2/FilePreview";
import { isMobileView } from "@/utils/screen";
import { isTMessage } from "@/i18n/tt";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { ReactElement } from "react";

export const BatchPreviewDialog = observer(function BatchPreviewDialog({
  model,
  onClose,
  customBody,
}: {
  model: BatchPreviewModel;
  onClose: () => void;
  customBody?: ReactElement;
}) {
  return (
    <Dialog fullscreen onCancel={onClose}>
      <FullScreenLayout
        pageHeader={<BatchPreviewHeader model={model} onClose={onClose} />}
        centerPanel={<BatchPreviewBody model={model} customBody={customBody} />}
      />
    </Dialog>
  );
});

const BatchPreviewHeader = observer(function SubscriptionFormsPreviewHeader({
  model,
  onClose,
}: {
  model: BatchPreviewModel;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      bg={"default"}
      alignItems={"center"}
      paddingLeft={"md"}
      paddingRight={"lg"}
      paddingY={"md"}
      borderBottom
    >
      <Box alignItems={"center"}>
        {onClose && (
          <IconButton icon={"x"} tooltip={t("close")} onClick={onClose} color={"inherit"} />
        )}
        <Box>{isTMessage(model.title) ? t(model.title) : model.title}</Box>
      </Box>
      <Box grow justifyContent={"end"} gap={"md"}>
        {model.canHandleDownload && (
          <>
            {isMobileView() ? (
              <IconButton
                icon={"download"}
                tooltip={{ key: "download" }}
                onClick={() => model.handleDownload(t)}
              />
            ) : (
              <Button
                className={"ml-auto"}
                icon={"download"}
                label={{ key: "download" }}
                variant={"secondary"}
                onClick={() => model.handleDownload(t)}
              />
            )}
          </>
        )}
        {model.showNavigationButtons && (
          <Box>
            <Select
              value={model.selectedFile}
              items={model.files}
              onSelect={(item) => model.selectFile(item)}
              idFn={(file) => file.reference}
              labelFn={(file) => file.name ?? ""}
            />
            {!isMobileView() && (
              <>
                <Button
                  variant={"basic"}
                  icon={"caret-left"}
                  tooltip={t("previous")}
                  onClick={model.previousDocument}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-right"}
                  tooltip={t("next")}
                  onClick={model.nextDocument}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});

const BatchPreviewBody = observer(function BatchPreviewBody({
  model,
  customBody,
}: {
  model: BatchPreviewModel;
  customBody?: ReactElement;
}) {
  const render = () => {
    switch (model.state.type) {
      case "loading":
        return <LoadingIndicator fullscreen />;
      case "error":
        return (
          <VBox grow={"golden"} justifyContent={"center"}>
            <EmptyState
              illustration={<Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />}
              title={{ key: "general-error" }}
            />
          </VBox>
        );
      case "ready":
        return customBody ?? <BatchPreviewReady file={model.file} />;
    }
  };
  return render();
});

export const BatchPreviewReady = observer(function BatchPreviewBody({
  file,
}: {
  file?: FilePreviewProps;
}) {
  if (file === undefined) {
    return (
      <VBox grow={"golden"} justifyContent={"center"}>
        <EmptyState illustration={"file"} title={{ key: "file-preview.missing-file" }} />
      </VBox>
    );
  }

  return <FilePreview {...file} />;
});
